import type { ReactElement } from 'react';
import type { MCPServerToolSummary } from '@kirin/api-client/contracts/mcp';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MCPToolsDialogProps {
  open: boolean;
  onClose: () => void;
  serverName: string;
  tools: MCPServerToolSummary[];
}

/**
 * Read-only viewer for the tools a remote MCP server exposes.
 * Sourced from session descriptors (Claude Desktop) or handshake
 * results (stdio/http transports). Empty descriptions are hidden.
 */
export function MCPToolsDialog({
  open,
  onClose,
  serverName,
  tools,
}: MCPToolsDialogProps): ReactElement {
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="leading-relaxed">
            Tools exposed by <span className="font-mono">{serverName}</span>
          </DialogTitle>
          <DialogDescription>
            {tools.length} tool{tools.length === 1 ? '' : 's'} reported by this
            MCP server.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex-1 min-h-0 overflow-y-auto rounded-md border">
          <ul className="divide-y">
            {tools.map((tool, i) => (
              // Index-suffixed key: a misbehaving server could report two
              // same-named tools, which would collide on name alone.
              <li key={`${tool.name}-${i}`} className="p-3">
                <div className="font-mono text-sm font-medium break-all">
                  {tool.name}
                </div>
                {tool.description && (
                  <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">
                    {tool.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
